import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiEye, FiEyeOff, FiArrowRight } from "react-icons/fi";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import "../../../../assets/css/equipmentrentaladmin/register/Register.css";
import AppContext from "../../../../context/AppContext";

// ==========================================================
// CONFIG: one entry per actor type
// - apiPrefix   -> used to build the backend URL
//                  (baseUrl + "/" + apiPrefix + "/authentication/forgotPassword")
// - loginPath   -> where "Remembered your password? Login" should redirect
// - heading     -> left panel title (branding text) shown per actor
// - subheading  -> left panel description text
//
// Adjust apiPrefix / loginPath values below to EXACTLY match
// your actual backend routes and frontend login routes for
// each actor. This is the only place you need to edit if a
// path changes later.
// ==========================================================
const USER_TYPE_CONFIG = {
  customer: {
    apiPrefix: "customer",
    loginPath: "/user/login", // adjust to your actual customer login route if different
    heading: "Reset Password",
    subheading: "Enter your email and we'll help you get back into your account.",
  },
  rentaladmin: {
    apiPrefix: "rentaladmin",
    loginPath: "/rental-admin/login",
    heading: "Reset Password",
    subheading: "No worries! Enter your email to reset your rental admin account password.",
  },
  mechanic: {
    apiPrefix: "mechanic",
    loginPath: "/mechanic/login",
    heading: "Reset Password",
    subheading: "Enter your registered email to reset your mechanic account password.",
  },
  admin: {
    apiPrefix: "admin",
    loginPath: "/admin/login",
    heading: "Reset Password",
    subheading: "Enter your registered email to reset your admin account password.",
  },
  superadmin: {
    apiPrefix: "superadmin",
    loginPath: "/superadmin/login",
    heading: "Reset Password",
    subheading: "Enter your registered email to reset your super admin account password.",
  },
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  // The route should be like: /forgot-password/:userType
  // e.g. /forgot-password/customer, /forgot-password/rentaladmin, etc.
  const { userType } = useParams();

  // Fallback to "customer" config if an unknown/missing userType is passed,
  // so the page never crashes even if the route is mistyped.
  const config = USER_TYPE_CONFIG[userType] || USER_TYPE_CONFIG.customer;

  // step: "email" -> "reset"
  const [step, setStep] = useState("email");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errormessage, setErrorMessage] = useState("");
  const [successmessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // STEP 1: Send OTP to email
  const sendOtp = () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!email) {
      setErrorMessage("Please enter your email address");
      return;
    }

    setLoading(true);

    const formdata = new FormData();
    formdata.append("email", email);

    const forgotUrl =
      config.forgotPasswordPath ||
      `/${config.apiPrefix}/authentication/forgotPassword`;

    axios
      .post(`${baseUrl}${forgotUrl}`, formdata)
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          setErrorMessage(response.data.error || "No account found with this email");
        } else {
          setSuccessMessage("OTP sent to your email");
          setStep("reset");
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorMessage(
          error?.response?.data?.error || "Something went wrong. Please try again."
        );
      });
  };

  // STEP 2: Verify OTP + set new password
  const resetPassword = () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!otp) {
      setErrorMessage("Please enter the OTP sent to your email");
      return;
    }
    if (!newPassword || !confirmPassword) {
      setErrorMessage("Please fill in both password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    setLoading(true);

    const formdata = new FormData();
    formdata.append("email", email);
    formdata.append("otp", otp);
    formdata.append("newPassword", newPassword);

    const resetUrl =
      config.resetPasswordPath ||
      `/${config.apiPrefix}/authentication/resetPassword`;

    axios
      .post(`${baseUrl}${resetUrl}`, formdata)
      .then((response) => {
        setLoading(false);
        if (response.data.error) {
          setErrorMessage(response.data.error || "Invalid or expired OTP");
        } else {
          setSuccessMessage("Password reset successfully. Redirecting to login...");
          setTimeout(() => {
            navigate(config.loginPath);
          }, 1500);
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorMessage(
          error?.response?.data?.error || "Something went wrong. Please try again."
        );
      });
  };

  // Resend OTP (reuses step 1 call)
  const resendOtp = () => {
    sendOtp();
  };

  return loading ? (
    <div
      style={{ height: "100vh" }}
      className="d-flex justify-content-center align-items-center"
    >
      <Spinner style={{ height: 60, width: 60, fontSize: 60 }} />
    </div>
  ) : (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f7f7f7",
        padding: "24px",
      }}
    >
      <div className="reg-form-wrapper" style={{ width: "100%", maxWidth: "480px" }}>
        <div className="reg-form-card">
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <span className="owner-badge">{userType?.toUpperCase() || "ACCOUNT"}</span>
          </div>

          {step === "email" ? (
            <>
              <h2>Forgot Password</h2>
              <p>Enter your registered email to receive an OTP.</p>

              <form>
                <div className="reg-form-group">
                  <label>Email Address</label>
                  <div className="reg-input-wrapper">
                    <input
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {errormessage && (
                  <p style={{ color: "red", fontSize: "0.8rem", marginTop: "6px" }}>
                    {errormessage}
                  </p>
                )}
                {successmessage && (
                  <p style={{ color: "green", fontSize: "0.8rem", marginTop: "6px" }}>
                    {successmessage}
                  </p>
                )}

                <button type="button" className="submit-btn" onClick={sendOtp}>
                  Send OTP <FiArrowRight />
                </button>
              </form>
            </>
          ) : (
            <>
              <h2>Reset Your Password</h2>
              <p>Enter the OTP sent to {email} and set a new password.</p>

              <form>
                <div className="reg-form-group">
                  <label>OTP</label>
                  <div className="reg-input-wrapper">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="reg-form-group">
                  <label>New Password</label>
                  <div className="reg-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <div className="reg-form-group">
                  <label>Confirm New Password</label>
                  <div className="reg-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                {errormessage && (
                  <p style={{ color: "red", fontSize: "0.8rem", marginTop: "6px" }}>
                    {errormessage}
                  </p>
                )}
                {successmessage && (
                  <p style={{ color: "green", fontSize: "0.8rem", marginTop: "6px" }}>
                    {successmessage}
                  </p>
                )}

                <button type="button" className="submit-btn" onClick={resetPassword}>
                  Reset Password <FiArrowRight />
                </button>

                <p
                  style={{
                    textAlign: "center",
                    marginTop: "18px",
                    fontSize: "0.875rem",
                    color: "#555",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Didn't get the OTP?{" "}
                  <span
                    onClick={resendOtp}
                    style={{
                      color: "#FDB714",
                      fontWeight: "600",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Resend
                  </span>
                </p>
              </form>
            </>
          )}

          <p
            style={{
              textAlign: "center",
              marginTop: "18px",
              fontSize: "0.875rem",
              color: "#555",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Remembered your password?{" "}
            <span
              onClick={() => navigate(config.loginPath)}
              style={{
                color: "#FDB714",
                fontWeight: "600",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Login
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}